import Client from '../client'
import config from '../config'

interface Channels {
  SHOPGOODWILL: Channel
  EBAY: Channel
  ETSY: Channel
  OFFER_UP: Channel
  FACEBOOK: Channel
  BLUEBOX: Channel
  SHOPIFY: Channel
  GOODWILLFINDS: Channel
  AMAZON: Channel
  DEPOP: Channel
}

export interface Channel {
  id: number
  name: string
  humanReadableName: string
  marketPlaceService?: boolean
  iconUrl: string
  errorKey: string
  listEndpoint: Function
  delistEndpoint: Function
  itemLink: (itemId: number | string, countryTLD?: string) => string
}

const CHANNELS: Channels = {
  SHOPGOODWILL: {
    id: 1,
    name: 'shopgoodwill',
    humanReadableName: 'Shopgoodwill',
    iconUrl: '/market_icons/thumbs/shopgoodwill.png',
    errorKey: 'sgwErrors',
    listEndpoint: Client.bulkListShopgoodwill,
    delistEndpoint: Client.bulkEndListingsShopgoodwill,
    itemLink: itemId => `https://www.shopgoodwill.com/item/${itemId}`
  },
  EBAY: {
    id: 2,
    name: 'ebay',
    humanReadableName: 'eBay',
    iconUrl: '/market_icons/ebay.gif',
    errorKey: 'ebayErrors',
    listEndpoint: Client.bulkListEbay,
    delistEndpoint: Client.bulkEndListingsEbay,
    itemLink: (itemId, countryTLD = 'com') =>
      config.STAGING ? `http://cgi.sandbox.ebay.com/itm/${itemId}` : `http://ebay.${countryTLD}/itm/${itemId}`
  },
  ETSY: {
    id: 11,
    name: 'etsy',
    humanReadableName: 'Etsy',
    iconUrl: '/market_icons/etsy.png',
    errorKey: 'ebayErrors',
    listEndpoint: (ids: string[]) => Client.bulkList(CHANNELS.ETSY.id, ids),
    delistEndpoint: (ids: string[]) => Client.bulkEndListings(CHANNELS.ETSY, ids),
    itemLink: itemId => `https://www.etsy.com/listing/${itemId}`
  },
  OFFER_UP: {
    id: 3,
    name: 'offer_up',
    humanReadableName: 'OfferUp',
    iconUrl: '/market_icons/thumbs/offer_up.png',
    errorKey: 'offerUpErrors',
    listEndpoint: Client.bulkListOfferUp,
    delistEndpoint: Client.bulkEndListingsOfferUp,
    itemLink: itemId =>
      config.STAGING ? `https://offerup-stg.com/item/detail/${itemId}` : `https://offerup.com/item/detail/${itemId}`
  },
  FACEBOOK: {
    id: 7,
    name: 'facebook',
    humanReadableName: 'Facebook',
    iconUrl: '/market_icons/facebook.png',
    errorKey: 'externalErrors',
    listEndpoint: Client.bulkListFacebook,
    delistEndpoint: Client.bulkEndListingsFacebook,
    itemLink: itemId => `https://facebook.com/${itemId}`
  },
  BLUEBOX: {
    id: 8,
    name: 'bluebox',
    humanReadableName: 'Bluebox',
    iconUrl: '/market_icons/thumbs/bluebox.png',
    errorKey: 'blueboxErrors',
    listEndpoint: null,
    delistEndpoint: null,
    itemLink: () => undefined
  },
  SHOPIFY: {
    id: 9,
    name: 'shopify',
    humanReadableName: 'Shopify',
    iconUrl: '/market_icons/thumbs/shopify.png',
    errorKey: 'shopifyErrors',
    listEndpoint: Client.bulkListShopify,
    delistEndpoint: Client.bulkEndListingsShopify,
    itemLink: () => undefined
  },
  GOODWILLFINDS: {
    id: 10,
    name: 'goodwillfinds',
    humanReadableName: 'GoodwillFinds',
    iconUrl: '/market_icons/thumbs/goodwillfinds.png',
    errorKey: 'marketErrors',
    listEndpoint: Client.bulkListGoodwillfinds,
    delistEndpoint: Client.bulkEndListingsGoodwillfinds,
    itemLink: itemId =>
      config.DEV
        ? `https://gwf-unified-catalog-webapp-git-dev-gwf-unifiedcatalog-webapp.vercel.app/search/${itemId}`
        : `https://www.goodwillfinds.com/${itemId}.html`
  },
  AMAZON: {
    id: 12,
    name: 'amazon',
    humanReadableName: 'Amazon',
    marketPlaceService: true,
    iconUrl: '/market_icons/thumbs/amazon.png',
    errorKey: 'marketErrors',
    listEndpoint: (ids: string[]) => Client.bulkList(CHANNELS.AMAZON.id, ids),
    delistEndpoint: (ids: string[]) => Client.bulkEndListings(CHANNELS.AMAZON, ids),
    itemLink: itemId => `https://www.amazon.com/dp/${itemId}`
  },
  DEPOP: {
    id: 13,
    name: 'depop',
    humanReadableName: 'Depop',
    marketPlaceService: true,
    iconUrl: '/market_icons/thumbs/depop.png',
    errorKey: 'marketErrors',
    listEndpoint: (ids: string[]) => Client.bulkList(CHANNELS.DEPOP.id, ids),
    delistEndpoint: (ids: string[]) => Client.bulkEndListings(CHANNELS.DEPOP, ids),
    itemLink: itemId => `https://www.depop.com/products/${itemId}/`
  }
}

export const deprecatedChannelIds = [CHANNELS.OFFER_UP.id, CHANNELS.FACEBOOK.id]

// for the brave, add this signature
// export function channelById(id: number): Channel | {}
export function channelById(id: number) {
  const found = Object.keys(CHANNELS).find(k => CHANNELS[k].id === id)
  if (found) return CHANNELS[found]
  return {}
}

export function channelByName(name: string) {
  const found = Object.keys(CHANNELS).find(k => CHANNELS[k].name === name)
  if (found) return CHANNELS[found]
  return {}
}

export default CHANNELS
